import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  Eye,
  EyeOff,
  AlertCircle,
  Shield,
  BarChart3,
  Calendar,
  Activity,
  Loader,
  RefreshCw,
  Settings,
  Info,
  Zap,
  Globe,
} from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";

const ApiKeyManager = () => {
  const { themeClasses } = useTheme();
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([
    "identify",
    "register",
    "health",
  ]);
  const [newApiKey, setNewApiKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [stats, setStats] = useState({});
  const [error, setError] = useState("");

  const functions = getFunctions(app);

  const availablePermissions = [
    { id: "identify", name: "Pet Identification", icon: Eye, color: "blue" },
    { id: "register", name: "Pet Registration", icon: Plus, color: "green" },
    { id: "health", name: "Health Records", icon: Activity, color: "purple" },
  ];

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setIsLoading(true);
      const listApiKeysFn = httpsCallable(functions, "listApiKeys");
      const response = await listApiKeysFn();

      if (response.data.success) {
        setApiKeys(response.data.data.apiKeys);
      } else {
        setError("Failed to load API keys");
      }
    } catch (error) {
      console.error("Error loading API keys:", error);
      setError("Failed to load API keys: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      setError("API key name is required");
      return;
    }

    try {
      setIsCreating(true);
      setError("");

      const createApiKeyFn = httpsCallable(functions, "createApiKey");
      const response = await createApiKeyFn({
        name: newKeyName,
        permissions: selectedPermissions,
      });

      if (response.data.success) {
        setNewApiKey(response.data.data.apiKey);
        setNewKeyName("");
        setShowCreateForm(false);
        await loadApiKeys();
      } else {
        setError("Failed to create API key");
      }
    } catch (error) {
      console.error("Error creating API key:", error);
      setError("Failed to create API key: " + error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const revokeApiKey = async (keyId, keyName) => {
    if (
      !confirm(
        `Are you sure you want to revoke the API key "${keyName}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const revokeApiKeyFn = httpsCallable(functions, "revokeApiKey");
      const response = await revokeApiKeyFn({ keyId });

      if (response.data.success) {
        await loadApiKeys();
      } else {
        setError("Failed to revoke API key");
      }
    } catch (error) {
      console.error("Error revoking API key:", error);
      setError("Failed to revoke API key: " + error.message);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(type);
    setTimeout(() => setCopiedKey(""), 2000);
  };

  const getUsageStats = async (keyId) => {
    try {
      const getStatsFn = httpsCallable(functions, "getApiKeyStats");
      const response = await getStatsFn({ keyId });

      if (response.data.success) {
        setStats({ ...stats, [keyId]: response.data.data });
      }
    } catch (error) {
      console.error("Error getting usage stats:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return themeClasses.text.success;
      case "revoked":
        return themeClasses.text.error;
      default:
        return themeClasses.text.tertiary;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="w-8 h-8 text-cyan-400 animate-spin" />
        <span className={`ml-3 ${themeClasses.text.primary}`}>
          Loading API keys...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2
            className={`text-2xl font-bold flex items-center space-x-3 ${themeClasses.text.primary}`}
          >
            <Key className="w-8 h-8 text-cyan-400" />
            <span>API Key Management</span>
          </h2>
          <p className={`mt-2 ${themeClasses.text.secondary}`}>
            Create and manage API keys for accessing the Pet ID API
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className={`${themeClasses.bg.button} text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 flex items-center space-x-2`}
        >
          <Plus className="w-5 h-5" />
          <span>Create API Key</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-300">{error}</span>
        </div>
      )}

      {/* New API Key Display */}
      {newApiKey && (
        <div
          className={`${themeClasses.bg.card} rounded-lg border p-6 border-green-500/50 ${themeClasses.border.primary}`}
        >
          <div className="flex items-center space-x-3 mb-4">
            <Shield className={`w-8 h-8 ${themeClasses.text.success}`} />
            <div>
              <h3 className={`text-xl font-bold ${themeClasses.text.primary}`}>
                API Key Created Successfully!
              </h3>
              <p className={themeClasses.text.secondary}>
                ⚠️ Save this key now - you won't be able to see it again
              </p>
            </div>
          </div>

          <div className={`rounded-lg p-4 mb-4 ${themeClasses.bg.secondary}`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label
                  className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                >
                  Your API Key
                </label>
                <div
                  className={`font-mono text-sm p-3 rounded border break-all ${themeClasses.text.primary} ${themeClasses.bg.tertiary}`}
                >
                  {showApiKey ? newApiKey : "•".repeat(newApiKey.length)}
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className={`p-2 transition-colors ${themeClasses.text.secondary} hover:${themeClasses.text.primary}`}
                >
                  {showApiKey ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={() => copyToClipboard(newApiKey, "new")}
                  className={`p-2 transition-colors ${themeClasses.text.secondary} hover:text-cyan-400`}
                >
                  {copiedKey === "new" ? (
                    <Check className={`w-5 h-5 ${themeClasses.text.success}`} />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={() => setNewApiKey(null)}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            I've Saved My API Key
          </button>
        </div>
      )}

      {/* Create API Key Form */}
      {showCreateForm && (
        <div
          className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
        >
          <h3
            className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
          >
            Create New API Key
          </h3>

          <div className="space-y-4">
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
              >
                API Key Name
              </label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg focus:border-cyan-500 focus:outline-none ${themeClasses.bg.input} ${themeClasses.border.primary} ${themeClasses.text.primary}`}
                placeholder="e.g., Mobile App Production"
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
              >
                Permissions
              </label>
              <div className="grid grid-cols-1 gap-2">
                {availablePermissions.map((permission) => (
                  <label
                    key={permission.id}
                    className="flex items-center space-x-3 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(permission.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPermissions([
                            ...selectedPermissions,
                            permission.id,
                          ]);
                        } else {
                          setSelectedPermissions(
                            selectedPermissions.filter(
                              (p) => p !== permission.id
                            )
                          );
                        }
                      }}
                      className="rounded"
                    />
                    <permission.icon
                      className={`w-5 h-5 text-${permission.color}-500`}
                    />
                    <span
                      className={`font-semibold ${themeClasses.text.primary}`}
                    >
                      {permission.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={createApiKey}
                disabled={isCreating || !newKeyName.trim()}
                className={`flex-1 ${themeClasses.bg.button} disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300`}
              >
                {isCreating ? (
                  <span className="flex items-center justify-center space-x-2">
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Creating...</span>
                  </span>
                ) : (
                  "Create API Key"
                )}
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                className={`px-4 py-2 rounded-lg transition-all duration-300 font-semibold ${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} border ${themeClasses.border.primary}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className={`text-xl font-semibold ${themeClasses.text.primary}`}>
            Your API Keys ({apiKeys.length})
          </h3>
          <button
            onClick={loadApiKeys}
            className={`p-2 transition-colors ${themeClasses.text.secondary} hover:text-cyan-400`}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {apiKeys.length === 0 ? (
          <div
            className={`${themeClasses.bg.card} rounded-lg border p-8 text-center ${themeClasses.border.primary}`}
          >
            <Key
              className={`w-16 h-16 mx-auto mb-4 ${themeClasses.text.secondary}`}
            />
            <h4
              className={`text-xl font-semibold mb-2 ${themeClasses.text.secondary}`}
            >
              No API Keys Yet
            </h4>
            <p className={`mb-4 ${themeClasses.text.secondary}`}>
              Create your first API key to start using the Pet ID API
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className={`${themeClasses.bg.button} text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300`}
            >
              Create Your First API Key
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {apiKeys.map((apiKey) => (
              <div
                key={apiKey.keyId}
                className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Key className="w-6 h-6 text-cyan-400" />
                    <div>
                      <h4
                        className={`text-lg font-semibold ${themeClasses.text.primary}`}
                      >
                        {apiKey.name}
                      </h4>
                      <p className={`text-sm ${themeClasses.text.secondary}`}>
                        Created {formatDate(apiKey.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`text-sm font-medium ${getStatusColor(
                        apiKey.status
                      )} capitalize`}
                    >
                      {apiKey.status}
                    </span>
                    {apiKey.status === "active" && (
                      <button
                        onClick={() => revokeApiKey(apiKey.keyId, apiKey.name)}
                        className={`p-2 transition-colors ${themeClasses.text.secondary} hover:text-red-400`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${themeClasses.text.secondary}`}
                    >
                      API Key
                    </label>
                    <div className="flex items-center space-x-2">
                      <code
                        className={`flex-1 text-sm px-2 py-1 rounded ${themeClasses.text.secondary} ${themeClasses.bg.secondary}`}
                      >
                        {apiKey.maskedKey}
                      </code>
                      <button
                        onClick={() =>
                          copyToClipboard(apiKey.maskedKey, apiKey.keyId)
                        }
                        className={`p-1 transition-colors ${themeClasses.text.secondary} hover:text-cyan-400`}
                      >
                        {copiedKey === apiKey.keyId ? (
                          <Check
                            className={`w-4 h-4 ${themeClasses.text.success}`}
                          />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${themeClasses.text.secondary}`}
                    >
                      Usage
                    </label>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className={themeClasses.text.secondary}>
                        <Zap className="w-4 h-4 inline mr-1" />
                        {apiKey.usageCount || 0} requests
                      </span>
                      <span className={themeClasses.text.secondary}>
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Last used: {formatDate(apiKey.lastUsed)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Permissions */}
                <div className="mb-4">
                  <label
                    className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                  >
                    Permissions
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {apiKey.permissions.map((permission) => {
                      const perm = availablePermissions.find(
                        (p) => p.id === permission
                      );
                      return (
                        <span
                          key={permission}
                          className={`inline-flex items-center space-x-1 bg-${
                            perm?.color || "gray"
                          }-500/20 text-${
                            perm?.color || "gray"
                          }-600 dark:text-${
                            perm?.color || "gray"
                          }-400 text-xs font-medium px-2 py-1 rounded-full`}
                        >
                          {perm && <perm.icon className="w-3 h-3" />}
                          <span>{perm?.name || permission}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Rate Limits */}
                <div
                  className={`flex items-center justify-between text-sm ${themeClasses.text.secondary}`}
                >
                  <div className="flex items-center space-x-4">
                    <span>
                      <Settings className="w-4 h-4 inline mr-1" />
                      Rate Limit: {apiKey.rateLimit?.requestsPerMinute || 100}
                      /min
                    </span>
                    <span>
                      <Globe className="w-4 h-4 inline mr-1" />
                      Daily: {apiKey.rateLimit?.requestsPerDay || 1000}/day
                    </span>
                  </div>
                  <button
                    onClick={() => getUsageStats(apiKey.keyId)}
                    className="text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    <BarChart3 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* API Documentation */}
      <div
        className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
      >
        <h3
          className={`text-xl font-semibold mb-4 flex items-center space-x-2 ${themeClasses.text.primary}`}
        >
          <Info className="w-6 h-6 text-cyan-400" />
          <span>API Usage</span>
        </h3>

        <div className="space-y-4">
          <div>
            <h4
              className={`text-lg font-medium mb-2 ${themeClasses.text.primary}`}
            >
              Authentication
            </h4>
            <p className={`mb-2 ${themeClasses.text.secondary}`}>
              Include your API key in the request header:
            </p>
            <code
              className={`block p-3 rounded text-sm font-mono ${themeClasses.bg.secondary} ${themeClasses.text.primary}`}
            >
              X-API-Key: pk_your_api_key_here
            </code>
          </div>

          <div>
            <h4
              className={`text-lg font-medium mb-2 ${themeClasses.text.primary}`}
            >
              Base URL
            </h4>
            <code
              className={`block p-3 rounded text-sm font-mono ${themeClasses.bg.secondary} ${themeClasses.text.primary}`}
            >
              https://us-central1-petid-77a78.cloudfunctions.net/api/
            </code>
          </div>

          <div>
            <h4
              className={`text-lg font-medium mb-2 ${themeClasses.text.primary}`}
            >
              Endpoints
            </h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">
                  POST
                </span>
                <code className={`${themeClasses.text.accent}`}>/identify</code>
                <span className={themeClasses.text.secondary}>
                  - Identify pet from image
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">
                  POST
                </span>
                <code className={`${themeClasses.text.accent}`}>/register</code>
                <span className={themeClasses.text.secondary}>
                  - Register new pet
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded">
                  POST
                </span>
                <code className={`${themeClasses.text.accent}`}>/health</code>
                <span className={themeClasses.text.secondary}>
                  - Update health records
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyManager;
